<template>
  <aside class="gm-panel roster-panel" :class="{ 'is-collapsed': collapsed }">
    <button
      v-if="collapsed"
      type="button"
      class="roster-panel__reopen"
      title="Show the roster"
      @click="$emit('toggle-collapse')"
    >
      <span class="roster-panel__reopen-glyph">›</span>
      <span class="roster-panel__reopen-text">Roster · {{ archetypes.length }}</span>
    </button>

    <template v-else>
      <header class="roster-panel__head">
        <input
          v-model="query"
          type="search"
          class="gm-input roster-panel__search"
          placeholder="Search archetypes"
          aria-label="Search archetypes"
        />
        <button
          type="button"
          class="roster-panel__collapse"
          title="Hide the roster and give the width to the build"
          aria-label="Hide the roster"
          @click="$emit('toggle-collapse')"
        >
          ‹
        </button>
      </header>

      <div class="roster-panel__filters">
        <div class="filter-set" role="group" aria-label="Filter by Path">
          <button
            v-for="option in pathOptions"
            :key="option.value"
            type="button"
            class="filter"
            :class="{ 'is-on': path === option.value }"
            @click="path = option.value"
          >
            {{ option.label }}
          </button>
        </div>

        <div class="filter-set" role="group" aria-label="Filter by metatype">
          <button
            v-for="option in metatypeOptions"
            :key="option"
            type="button"
            class="filter filter--metatype"
            :class="[{ 'is-on': metatype === option }, `is-${option.toLowerCase()}`]"
            :disabled="option !== 'All' && !counts[option]"
            @click="metatype = option"
          >
            <MetatypeIcon v-if="option !== 'All'" :metatype="option" />
            <span>{{ option }}</span>
            <span v-if="option !== 'All'" class="filter__n">{{ counts[option] ?? 0 }}</span>
          </button>
        </div>
      </div>

      <div class="roster-panel__grid gm-scroll">
        <ArchetypeCard
          v-for="archetype in filtered"
          :key="archetype.id"
          :archetype="archetype"
          :selected="selectedIds.includes(archetype.id)"
          :disabled="selectedIds.length >= maxSelection"
          @toggle="$emit('toggle', archetype.id)"
        />

        <p v-if="!filtered.length" class="roster-panel__empty">
          Nothing matches those filters. Clear one to see the rest of the roster.
        </p>
      </div>

      <p class="roster-panel__count">
        {{ filtered.length }} shown · {{ selectedIds.length }}/{{ maxSelection }} open
      </p>
    </template>
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue';
import ArchetypeCard from './ArchetypeCard.vue';
import MetatypeIcon from './MetatypeIcon.vue';

const props = defineProps({
  archetypes: { type: Array, required: true },
  selectedIds: { type: Array, default: () => [] },
  maxSelection: { type: Number, default: 6 },
  collapsed: { type: Boolean, default: false },
});

defineEmits(['toggle', 'toggle-collapse']);

const query = ref('');
const path = ref('All');
const metatype = ref('All');

const metatypeOptions = ['All', 'Attacker', 'Defender', 'Healer', 'Buffer', 'Debuffer'];

// The full Path names are long and the panel is narrow, so the filter shows the
// short form the rest of the tool uses.
const pathOptions = computed(() => {
  const paths = [...new Set(props.archetypes.map((a) => a.path).filter(Boolean))].sort();
  return [
    { value: 'All', label: 'Both Paths' },
    ...paths.map((value) => ({ value, label: value.replace('Path of ', '') })),
  ];
});

const counts = computed(() =>
  props.archetypes.reduce((tally, archetype) => {
    tally[archetype.metatype] = (tally[archetype.metatype] ?? 0) + 1;
    return tally;
  }, {}),
);

const filtered = computed(() => {
  const needle = query.value.trim().toLowerCase();
  return props.archetypes.filter((archetype) => {
    if (path.value !== 'All' && archetype.path !== path.value) return false;
    if (metatype.value !== 'All' && archetype.metatype !== metatype.value) return false;
    if (!needle) return true;
    return (
      archetype.name.toLowerCase().includes(needle) ||
      (archetype.description ?? '').toLowerCase().includes(needle)
    );
  });
});
</script>

<style scoped>
.roster-panel {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  flex: 0 0 19rem;
  width: 19rem;
  height: 100%;
  min-height: 0;
  padding: var(--gm-gap-sm);
  transition: flex-basis 0.18s ease, width 0.18s ease;
}

/* Collapsing the roster is the cheapest way to give a build the whole width,
   and the tool is used that way: pick two or three, then read them. */
.roster-panel.is-collapsed {
  flex-basis: 2rem;
  width: 2rem;
  padding: 0;
}

.roster-panel__reopen {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gm-gap);
  width: 100%;
  height: 100%;
  padding: var(--gm-gap-sm) 0;
  font: inherit;
  color: var(--gm-text-muted);
  background: transparent;
  border: none;
  cursor: pointer;
}

.roster-panel__reopen:hover {
  color: var(--gm-accent);
}

.roster-panel__reopen-glyph {
  font-size: 1rem;
  line-height: 1;
}

.roster-panel__reopen-text {
  writing-mode: vertical-rl;
  font-family: var(--gm-font-display);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
}

.roster-panel__head {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-xs);
}

.roster-panel__search {
  flex: 1;
  min-width: 0;
}

.roster-panel__collapse {
  flex: 0 0 auto;
  width: 1.6rem;
  height: 1.6rem;
  display: grid;
  place-items: center;
  font: inherit;
  font-size: 1rem;
  line-height: 1;
  color: var(--gm-text-muted);
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
}

.roster-panel__collapse:hover {
  color: var(--gm-text);
  border-color: var(--gm-border);
}

.roster-panel__count {
  margin: 0;
  font-family: var(--gm-font-display);
  font-size: 0.55rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gm-text-dim);
  white-space: nowrap;
}

.roster-panel__filters {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-xs);
}

.filter-set {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-xs);
}

/* Deliberately not the shared .gm-btn: these are a filter bar, and reading as
   six equally-weighted buttons is what made the old one look like a toolbar
   rather than a set of toggles. */
.filter {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.18rem 0.4rem;
  font-family: var(--gm-font-display);
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--gm-text-muted);
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  cursor: pointer;
  transition: color 0.15s ease, border-color 0.15s ease;
}

.filter:hover:not(:disabled) {
  color: var(--gm-text);
  border-color: var(--gm-border);
}

.filter:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.filter.is-on {
  color: var(--gm-surface-0);
  background: var(--gm-accent);
  border-color: var(--gm-accent);
}

/* Off state carries the metatype's own colour on the mark, so the filter bar
   doubles as the legend for the plates below it. */
.filter--metatype :deep(.metatype-icon) {
  color: var(--filter-color, currentColor);
  width: 0.85em;
  height: 0.85em;
}

.filter.is-on :deep(.metatype-icon) {
  color: currentColor;
}

.filter.is-attacker { --filter-color: #d8635c; }
.filter.is-defender { --filter-color: var(--gm-info); }
.filter.is-healer { --filter-color: var(--gm-success); }
.filter.is-buffer { --filter-color: var(--gm-accent); }
.filter.is-debuffer { --filter-color: #a888c9; }

.filter__n {
  color: var(--gm-text-dim);
  font-weight: 500;
}

.filter.is-on .filter__n {
  color: rgba(12, 14, 15, 0.7);
}

.roster-panel__grid {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  /* Roughly 4:5 against the track width at this panel size — set on the track
     because the plate is a button, and Chromium will not honour aspect-ratio
     on one. */
  grid-auto-rows: 10.5rem;
  gap: var(--gm-gap-xs);
  padding-right: 0.25rem;
  align-content: start;
}

.roster-panel__empty {
  grid-column: 1 / -1;
  margin: 0;
  padding: var(--gm-gap);
  font-size: 0.75rem;
  color: var(--gm-text-dim);
}

@media (max-width: 1400px) {
  .roster-panel {
    flex-basis: 16.5rem;
    width: 16.5rem;
  }

  .roster-panel__grid {
    grid-auto-rows: 9rem;
  }
}
</style>
